// Database structure and helper functions for Firestore

import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// Collection names
export const COLLECTIONS = {
  PRACTICES: 'practices',
  APPOINTMENTS: 'appointments',
  AVAILABILITY: 'availability',
  USERS: 'users',
};

// Practice document structure
/*
  Practice: {
    id: string,
    name: string,
    address: {
      line1: string,
      line2: string (optional),
      city: string,
      postcode: string,
    },
    contact: {
      phone: string,
      email: string,
      website: string (optional),
    },
    services: array of strings,
    operatives: array of user references,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
*/

// Availability document structure
/*
  Availability: {
    id: string,
    practiceId: reference to practice,
    operativeId: reference to user,
    date: timestamp (date only),
    slots: [
      {
        startTime: timestamp,
        endTime: timestamp,
        isBooked: boolean,
        appointmentId: string (optional, reference to appointment if booked),
      }
    ],
    createdAt: timestamp,
    updatedAt: timestamp,
  }
*/

// Appointment document structure
/*
  Appointment: {
    id: string,
    practiceId: reference to practice,
    operativeId: reference to user,
    patientId: reference to user,
    startTime: timestamp,
    endTime: timestamp,
    service: string,
    status: string (scheduled, completed, cancelled),
    notes: string (optional),
    createdAt: timestamp,
    updatedAt: timestamp,
  }
*/

// User document structure
/*
  User: {
    id: string,
    email: string,
    displayName: string,
    role: string (admin, operative, patient),
    phone: string (optional),
    practices: array of practice references (for operatives),
    createdAt: timestamp,
    updatedAt: timestamp,
  }
*/

// Practice CRUD operations
export const practiceService = {
  // Create a new practice
  createPractice: async (practiceData) => {
    try {
      const practicesRef = collection(db, COLLECTIONS.PRACTICES);
      const now = Timestamp.now();
      
      const newPractice = {
        ...practiceData,
        createdAt: now,
        updatedAt: now,
      };
      
      const docRef = await addDoc(practicesRef, newPractice);
      return { id: docRef.id, ...newPractice };
    } catch (error) {
      console.error('Error creating practice:', error);
      throw error;
    }
  },
  
  // Get a practice by ID
  getPractice: async (practiceId) => {
    try {
      const practiceRef = doc(db, COLLECTIONS.PRACTICES, practiceId);
      const practiceSnap = await getDoc(practiceRef);
      
      if (practiceSnap.exists()) {
        return { id: practiceSnap.id, ...practiceSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting practice:', error);
      throw error;
    }
  },
  
  // Get all practices
  getAllPractices: async () => {
    try {
      const practicesRef = collection(db, COLLECTIONS.PRACTICES);
      const querySnapshot = await getDocs(practicesRef);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all practices:', error);
      throw error;
    }
  },
  
  // Update a practice
  updatePractice: async (practiceId, practiceData) => {
    try {
      const practiceRef = doc(db, COLLECTIONS.PRACTICES, practiceId);
      
      const updatedData = {
        ...practiceData,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(practiceRef, updatedData);
      return { id: practiceId, ...updatedData };
    } catch (error) {
      console.error('Error updating practice:', error);
      throw error;
    }
  },
  
  // Delete a practice
  deletePractice: async (practiceId) => {
    try {
      const practiceRef = doc(db, COLLECTIONS.PRACTICES, practiceId);
      await deleteDoc(practiceRef);
      return true;
    } catch (error) {
      console.error('Error deleting practice:', error);
      throw error;
    }
  },
  
  // Import multiple practices
  importPractices: async (practicesData) => {
    try {
      const batch = [];
      const now = Timestamp.now();
      
      for (const practiceData of practicesData) {
        const newPractice = {
          ...practiceData,
          createdAt: now,
          updatedAt: now,
        };
        
        batch.push(
          addDoc(collection(db, COLLECTIONS.PRACTICES), newPractice)
        );
      }
      
      const results = await Promise.all(batch);
      return results.map(docRef => docRef.id);
    } catch (error) {
      console.error('Error importing practices:', error);
      throw error;
    }
  },
};

// Availability CRUD operations
export const availabilityService = {
  // Create availability for a practice and operative
  createAvailability: async (availabilityData) => {
    try {
      const availabilityRef = collection(db, COLLECTIONS.AVAILABILITY);
      const now = Timestamp.now();
      
      const newAvailability = {
        ...availabilityData,
        createdAt: now,
        updatedAt: now,
      };
      
      const docRef = await addDoc(availabilityRef, newAvailability);
      return { id: docRef.id, ...newAvailability };
    } catch (error) {
      console.error('Error creating availability:', error);
      throw error;
    }
  },
  
  // Get availability for a practice on a specific date
  getPracticeAvailability: async (practiceId, date) => {
    try {
      const availabilityRef = collection(db, COLLECTIONS.AVAILABILITY);
      const q = query(
        availabilityRef,
        where('practiceId', '==', practiceId),
        where('date', '==', date)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting practice availability:', error);
      throw error;
    }
  },
  
  // Get availability for an operative on a specific date
  getOperativeAvailability: async (operativeId, date) => {
    try {
      const availabilityRef = collection(db, COLLECTIONS.AVAILABILITY);
      const q = query(
        availabilityRef,
        where('operativeId', '==', operativeId),
        where('date', '==', date)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting operative availability:', error);
      throw error;
    }
  },
  
  // Update availability
  updateAvailability: async (availabilityId, availabilityData) => {
    try {
      const availabilityRef = doc(db, COLLECTIONS.AVAILABILITY, availabilityId);
      
      const updatedData = {
        ...availabilityData,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(availabilityRef, updatedData);
      return { id: availabilityId, ...updatedData };
    } catch (error) {
      console.error('Error updating availability:', error);
      throw error;
    }
  },
  
  // Delete availability
  deleteAvailability: async (availabilityId) => {
    try {
      const availabilityRef = doc(db, COLLECTIONS.AVAILABILITY, availabilityId);
      await deleteDoc(availabilityRef);
      return true;
    } catch (error) {
      console.error('Error deleting availability:', error);
      throw error;
    }
  },
};

// Appointment CRUD operations
export const appointmentService = {
  // Create a new appointment
  createAppointment: async (appointmentData) => {
    try {
      const appointmentsRef = collection(db, COLLECTIONS.APPOINTMENTS);
      const now = Timestamp.now();
      
      const newAppointment = {
        ...appointmentData,
        status: 'scheduled',
        createdAt: now,
        updatedAt: now,
      };
      
      const docRef = await addDoc(appointmentsRef, newAppointment);
      return { id: docRef.id, ...newAppointment };
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },
  
  // Get an appointment by ID
  getAppointment: async (appointmentId) => {
    try {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      const appointmentSnap = await getDoc(appointmentRef);
      
      if (appointmentSnap.exists()) {
        return { id: appointmentSnap.id, ...appointmentSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting appointment:', error);
      throw error;
    }
  },
  
  // Get appointments for a practice
  getPracticeAppointments: async (practiceId) => {
    try {
      const appointmentsRef = collection(db, COLLECTIONS.APPOINTMENTS);
      const q = query(
        appointmentsRef,
        where('practiceId', '==', practiceId),
        orderBy('startTime')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting practice appointments:', error);
      throw error;
    }
  },
  
  // Get appointments for an operative
  getOperativeAppointments: async (operativeId) => {
    try {
      const appointmentsRef = collection(db, COLLECTIONS.APPOINTMENTS);
      const q = query(
        appointmentsRef,
        where('operativeId', '==', operativeId),
        orderBy('startTime')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting operative appointments:', error);
      throw error;
    }
  },
  
  // Get appointments for a patient
  getPatientAppointments: async (patientId) => {
    try {
      const appointmentsRef = collection(db, COLLECTIONS.APPOINTMENTS);
      const q = query(
        appointmentsRef,
        where('patientId', '==', patientId),
        orderBy('startTime')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting patient appointments:', error);
      throw error;
    }
  },
  
  // Update an appointment
  updateAppointment: async (appointmentId, appointmentData) => {
    try {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      
      const updatedData = {
        ...appointmentData,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(appointmentRef, updatedData);
      return { id: appointmentId, ...updatedData };
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  },
  
  // Cancel an appointment
  cancelAppointment: async (appointmentId) => {
    try {
      const appointmentRef = doc(db, COLLECTIONS.APPOINTMENTS, appointmentId);
      
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        updatedAt: Timestamp.now(),
      });
      
      return true;
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw error;
    }
  },
};

// Helper functions for date and time operations
export const dateTimeHelpers = {
  // Create a Timestamp for a specific date (without time)
  createDateTimestamp: (year, month, day) => {
    // Month is 0-indexed in JavaScript Date
    return Timestamp.fromDate(new Date(year, month - 1, day, 0, 0, 0));
  },
  
  // Create a Timestamp for a specific time on a specific date
  createTimeTimestamp: (year, month, day, hour, minute) => {
    // Month is 0-indexed in JavaScript Date
    return Timestamp.fromDate(new Date(year, month - 1, day, hour, minute, 0));
  },
  
  // Generate time slots for a day (e.g., 9:00 AM to 5:00 PM in 30-minute intervals)
  generateTimeSlots: (date, startHour, endHour, intervalMinutes) => {
    const slots = [];
    const baseDate = date.toDate();
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const day = baseDate.getDate();
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const startTime = Timestamp.fromDate(new Date(year, month, day, hour, minute, 0));
        const endTime = Timestamp.fromDate(new Date(year, month, day, hour, minute + intervalMinutes, 0));
        
        slots.push({
          startTime,
          endTime,
          isBooked: false,
        });
      }
    }
    
    return slots;
  },
};
